import { paymentStatementApi } from './base';

export interface Enterprise {
	id: string;
	enterprise_id: string;
	company_name: string;
	active: boolean;
	user_count: number;
	code?: string | null;
	region?: string | null;
	contact_email?: string | null;
	contact_phone?: string | null;
	created_at?: string | null;
	updated_at?: string | null;
	last_login_at?: string | null;
}

export interface EnterpriseUser {
	id: string;
	email: string;
	name: string;
	roles: string[];
	status: string;
	email_verified: boolean;
	created_at?: string | null;
	last_login_at?: string | null;
}

export interface EnterpriseDetail extends Enterprise {
	description?: string | null;
	logo_base64?: string | null;
	address: {
		street?: string | null;
		city?: string | null;
		state?: string | null;
		zip?: string | null;
		country?: string | null;
	};
	contact: {
		phone?: string | null;
		email?: string | null;
		website?: string | null;
	};
	users: EnterpriseUser[];
}

export interface EnterprisesResponse {
	success: boolean;
	message: string;
	enterprises: Enterprise[];
	total: number;
}

export interface EnterpriseResponse {
	success: boolean;
	message: string;
	enterprise: EnterpriseDetail;
}

export interface UpdateEnterpriseStatusRequest {
	active: boolean;
}

export interface UpdateUserRequest {
	role?: string;
	active?: boolean;
	name?: string;
}

export interface UpdateUserResponse {
	success: boolean;
	message: string;
	user: {
		id: string;
		email: string;
		name: string;
		roles: string[];
		status: string;
	};
}

export interface CreateEnterpriseRequest {
	company_name: string;
	contact_email?: string;
	contact_phone?: string;
	description?: string;
	active?: boolean;
}

export interface InviteUserRequest {
	email: string;
	name?: string;
	role: string;
}

export interface InviteUserResponse {
	success: boolean;
	message: string;
	invite: {
		id: string;
		email: string;
		status: string;
		role: string;
		expires_at?: string;
		created_at?: string;
	};
}

export interface BulkUpdateUserRolesRequest {
	updates: Array<{
		user_id: string;
		role: string;
	}>;
}

export interface BulkUpdateUserRolesResponse {
	success: boolean;
	message: string;
	updated: Array<{
		id: string;
		name: string;
		email: string;
		roles: string[];
		enterprise_id: string;
		status: string;
		last_login?: string;
	}>;
	failed?: Array<{
		user_id: string;
		error: string;
	}>;
}

export interface ListEnterprisesParams {
	active_only?: boolean;
	last_login_since?: string; // ISO date string (from date)
	last_login_before?: string; // ISO date string (to date, optional)
}

export const adminApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		listEnterprises: builder.query<
			EnterprisesResponse,
			ListEnterprisesParams | void
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				if (params?.active_only !== undefined) {
					queryParams.append('active_only', params.active_only.toString());
				}
				if (params?.last_login_since) {
					queryParams.append('last_login_since', params.last_login_since);
				}
				if (params?.last_login_before) {
					queryParams.append('last_login_before', params.last_login_before);
				}
				const queryString = queryParams.toString();
				return {
					url: `/admin/enterprises${queryString ? `?${queryString}` : ''}`,
					method: 'GET',
				};
			},
			providesTags: ['Enterprises'],
		}),

		getEnterprise: builder.query<EnterpriseResponse, string>({
			query: (enterpriseId) => ({
				url: `/admin/enterprises/${enterpriseId}`,
				method: 'GET',
			}),
			providesTags: (result, error, enterpriseId) => [
				{ type: 'Enterprises', id: enterpriseId },
			],
		}),

		updateEnterpriseStatus: builder.mutation<
			EnterpriseResponse,
			{ enterpriseId: string; data: UpdateEnterpriseStatusRequest }
		>({
			query: ({ enterpriseId, data }) => ({
				url: `/admin/enterprises/${enterpriseId}/status`,
				method: 'PATCH',
				body: data,
			}),
			invalidatesTags: (result, error, { enterpriseId }) => [
				'Enterprises',
				{ type: 'Enterprises', id: enterpriseId },
			],
		}),

		updateAdminUser: builder.mutation<
			UpdateUserResponse,
			{ userId: string; data: UpdateUserRequest }
		>({
			query: ({ userId, data }) => ({
				url: `/admin/users/${userId}`,
				method: 'PATCH',
				body: data,
			}),
			invalidatesTags: (result, error, { userId }) => [
				'Enterprises',
				'EnterpriseUsers',
			],
		}),

		bulkUpdateUserRoles: builder.mutation<
			BulkUpdateUserRolesResponse,
			BulkUpdateUserRolesRequest
		>({
			query: (data) => ({
				url: '/admin/users/bulk-update-roles',
				method: 'PATCH',
				body: data,
			}),
			invalidatesTags: ['Enterprises', 'EnterpriseUsers'],
		}),

		createEnterprise: builder.mutation<
			EnterpriseResponse,
			CreateEnterpriseRequest
		>({
			query: (data) => ({
				url: '/admin/enterprises',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Enterprises'],
		}),

		inviteUserToEnterprise: builder.mutation<
			InviteUserResponse,
			{ enterpriseId: string; data: InviteUserRequest }
		>({
			query: ({ enterpriseId, data }) => ({
				url: `/admin/enterprises/${enterpriseId}/invite`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: (result, error, { enterpriseId }) => [
				{ type: 'Enterprises', id: enterpriseId },
				'EnterpriseUsers',
			],
		}),
	}),
});

export const {
	useListEnterprisesQuery,
	useGetEnterpriseQuery,
	useUpdateEnterpriseStatusMutation,
	useUpdateAdminUserMutation,
	useBulkUpdateUserRolesMutation,
	useCreateEnterpriseMutation,
	useInviteUserToEnterpriseMutation,
} = adminApi;
